import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Command } from 'cmdk';
import type { LucideIcon } from 'lucide-react';
import {
  Briefcase,
  Building2,
  CheckSquare,
  CornerDownLeft,
  Loader2,
  Monitor,
  Moon,
  PanelLeft,
  Plus,
  Search,
  Sparkles,
  Sun,
  UserCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useDebounce } from '@/hooks/useDebounce';
import { usePermissions } from '@/hooks/usePermissions';
import { useUIStore } from '@/stores/uiStore';
import {
  EMPTY_SEARCH_RESULTS,
  globalSearch,
  type SearchHit,
  type SearchHitType,
} from '@/services/global-search.service';
import { toIlikePattern } from '@/services/globalSearchQuery';
import {
  ADMIN_DESTINATION,
  NAVIGATION,
  SECONDARY_DESTINATIONS,
  type NavItem,
} from './navigation';
import { emitShellEvent, useShellEvent } from './shellEvents';

/**
 * The command palette: jump to any page, start a new record, or find a
 * property, deal or contact by name — from anywhere, without the mouse.
 *
 *   ⌘K / Ctrl+K   open from anywhere, including inside a text field
 *   /             open, when the keyboard is not already typing somewhere
 *
 * Pages and actions are matched here, on every keystroke. Records are matched
 * by the database, debounced, under the caller's own session.
 */

interface Action {
  id: string;
  label: string;
  icon: LucideIcon;
  keywords?: string[];
  run: () => void;
}

const HIT_ICON: Record<SearchHitType, LucideIcon> = {
  property: Building2,
  deal: Briefcase,
  contact: UserCircle,
};

/** Every word typed must appear somewhere in the label or its keywords. */
function matches(query: string, label: string, keywords: string[] = []): boolean {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const haystack = [label, ...keywords].join(' ').toLowerCase();
  return words.every((word) => haystack.includes(word));
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.isContentEditable ||
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement
  );
}

const groupClass = cn(
  'px-2 pb-1',
  '[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-3',
  '[&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-semibold',
  '[&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-widest',
  '[&_[cmdk-group-heading]]:text-gray-400 dark:[&_[cmdk-group-heading]]:text-gray-500'
);

const itemClass = cn(
  'flex cursor-pointer select-none items-center gap-3 rounded-lg px-2.5 py-2 text-sm',
  'text-gray-700 dark:text-gray-300',
  'data-[selected=true]:bg-gray-100 data-[selected=true]:text-gray-900',
  'dark:data-[selected=true]:bg-gray-800 dark:data-[selected=true]:text-gray-100'
);

function Row({
  value,
  icon: Icon,
  label,
  detail,
  hint,
  onSelect,
}: {
  value: string;
  icon: LucideIcon;
  label: string;
  detail?: string | null;
  hint?: string;
  onSelect: () => void;
}) {
  return (
    <Command.Item value={value} onSelect={onSelect} className={itemClass}>
      <Icon className="h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500" aria-hidden="true" />
      <span className="min-w-0 flex-1 truncate">
        <span className="font-medium">{label}</span>
        {detail && <span className="ml-2 text-gray-400 dark:text-gray-500">{detail}</span>}
      </span>
      {hint && (
        <span className="shrink-0 text-[11px] font-medium text-gray-400 dark:text-gray-500">
          {hint}
        </span>
      )}
    </Command.Item>
  );
}

export function CommandPalette() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query.trim(), 250);
  const { isAdminUser } = usePermissions();
  const theme = useUIStore((s) => s.theme);
  const setTheme = useUIStore((s) => s.setTheme);
  const toggleSidebar = useUIStore((s) => s.toggleSidebar);

  useShellEvent('open-command-palette', () => setOpen(true));

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen((current) => !current);
        return;
      }
      if (
        event.key === '/' &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.altKey &&
        !isTypingTarget(event.target)
      ) {
        event.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Start clean each time: yesterday's query is not today's question.
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const searchable = toIlikePattern(debouncedQuery) !== null;
  const { data: hits = EMPTY_SEARCH_RESULTS, isFetching } = useQuery({
    queryKey: ['global-search', debouncedQuery],
    queryFn: () => globalSearch(debouncedQuery),
    enabled: open && searchable,
    staleTime: 30_000,
    // Keep the last answer on screen while the next one loads, so the list
    // does not blink empty between keystrokes.
    placeholderData: keepPreviousData,
  });
  // The debounce trails the input; do not show results for text that has
  // already been cleared.
  const showHits = searchable && toIlikePattern(query) !== null;
  const waiting = showHits && (isFetching || debouncedQuery !== query.trim());

  const go = (href: string) => {
    setOpen(false);
    navigate(href);
  };
  const run = (action: () => void) => {
    setOpen(false);
    action();
  };

  const actions = useMemo<Action[]>(
    () => [
      { id: 'new-deal', label: 'New deal', icon: Briefcase, keywords: ['create', 'add'], run: () => navigate('/deals/new') },
      { id: 'new-property', label: 'New property', icon: Building2, keywords: ['create', 'add'], run: () => navigate('/properties/new') },
      { id: 'new-contact', label: 'New contact', icon: UserCircle, keywords: ['create', 'add', 'person'], run: () => navigate('/contacts/new') },
      { id: 'new-task', label: 'New task', icon: CheckSquare, keywords: ['create', 'add', 'todo'], run: () => navigate('/tasks/new') },
      { id: 'ai-import', label: 'AI import from files', icon: Sparkles, keywords: ['upload', 'om', 'offering memorandum'], run: () => emitShellEvent('open-ai-import') },
    ],
    [navigate]
  );

  const preferences = useMemo<Action[]>(
    () => [
      { id: 'theme-light', label: 'Use light theme', icon: Sun, keywords: ['appearance', 'mode'], run: () => setTheme('light') },
      { id: 'theme-dark', label: 'Use dark theme', icon: Moon, keywords: ['appearance', 'mode', 'night'], run: () => setTheme('dark') },
      { id: 'theme-system', label: 'Match system theme', icon: Monitor, keywords: ['appearance', 'mode', 'auto'], run: () => setTheme('system') },
      { id: 'toggle-sidebar', label: 'Collapse or expand sidebar', icon: PanelLeft, keywords: ['navigation', 'menu'], run: toggleSidebar },
    ].filter((action) => action.id !== `theme-${theme}`),
    [setTheme, theme, toggleSidebar]
  );

  const primaryPages = NAVIGATION.flatMap((section) => section.items).filter((item) =>
    matches(query, item.label, item.keywords)
  );
  const secondaryGroups = SECONDARY_DESTINATIONS.map((group) => {
    const items: NavItem[] =
      group.group === 'Account' && isAdminUser ? [...group.items, ADMIN_DESTINATION] : group.items;
    // With nothing typed the palette shows the places people go, not all 40.
    return {
      group: group.group,
      items: query.trim() ? items.filter((item) => matches(query, item.label, item.keywords)) : [],
    };
  }).filter((group) => group.items.length > 0);
  const visibleActions = actions.filter((action) => matches(query, action.label, action.keywords));
  const visiblePreferences = query.trim()
    ? preferences.filter((action) => matches(query, action.label, action.keywords))
    : [];

  const recordGroups: { heading: string; items: SearchHit[] }[] = showHits
    ? [
        { heading: 'Properties', items: hits.properties },
        { heading: 'Deals', items: hits.deals },
        { heading: 'Contacts', items: hits.contacts },
      ].filter((group) => group.items.length > 0)
    : [];

  return (
    <Command.Dialog
      open={open}
      onOpenChange={setOpen}
      label="Search Flow"
      shouldFilter={false}
      loop
      overlayClassName="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm animate-fade-in-plain"
      contentClassName={cn(
        'fixed left-1/2 top-[12vh] z-[101] w-[calc(100%-2rem)] max-w-xl -translate-x-1/2',
        'overflow-hidden rounded-2xl bg-white shadow-overlay',
        'dark:bg-gray-900 dark:ring-1 dark:ring-white/10',
        'focus:outline-none'
      )}
    >
      <div className="flex items-center gap-3 border-b border-gray-100 px-4 dark:border-gray-800">
        {waiting ? (
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-primary-500" aria-hidden="true" />
        ) : (
          <Search className="h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500" aria-hidden="true" />
        )}
        <Command.Input
          value={query}
          onValueChange={setQuery}
          placeholder="Search properties, deals and contacts, or jump to a page…"
          className="h-12 w-full border-0 bg-transparent text-[15px] text-gray-900 shadow-none outline-none placeholder:text-gray-400 focus:shadow-none dark:text-gray-100 dark:placeholder:text-gray-500"
        />
        <kbd className="hidden shrink-0 rounded-md border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[11px] font-medium text-gray-500 sm:block dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
          Esc
        </kbd>
      </div>

      <Command.List className="max-h-[min(60vh,26rem)] overflow-y-auto overscroll-contain py-1">
        <Command.Empty className="px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
          {waiting ? 'Searching…' : `Nothing matches “${query.trim()}”.`}
        </Command.Empty>

        {recordGroups.map((group) => (
          <Command.Group key={group.heading} heading={group.heading} className={groupClass}>
            {group.items.map((hit) => (
              <Row
                key={`${hit.type}-${hit.id}`}
                value={`${hit.type}-${hit.id}`}
                icon={HIT_ICON[hit.type]}
                label={hit.title}
                detail={hit.subtitle}
                onSelect={() => go(hit.href)}
              />
            ))}
          </Command.Group>
        ))}

        {/* Once something is typed, each page says what it is for — "Letter
            Desk" alone did not tell anyone what lands there. With nothing
            typed the list stays the bare menu it has always been. */}
        {primaryPages.length > 0 && (
          <Command.Group heading="Go to" className={groupClass}>
            {primaryPages.map((item) => (
              <Row
                key={item.href}
                value={`page-${item.href}`}
                icon={item.icon}
                label={item.label}
                detail={query.trim() ? item.description : undefined}
                onSelect={() => go(item.href)}
              />
            ))}
          </Command.Group>
        )}

        {secondaryGroups.map((group) => (
          <Command.Group key={group.group} heading={group.group} className={groupClass}>
            {group.items.map((item) => (
              <Row
                key={item.href}
                value={`page-${item.href}`}
                icon={item.icon}
                label={item.label}
                detail={item.description}
                onSelect={() => go(item.href)}
              />
            ))}
          </Command.Group>
        ))}

        {visibleActions.length > 0 && (
          <Command.Group heading="Create" className={groupClass}>
            {visibleActions.map((action) => (
              <Row
                key={action.id}
                value={`action-${action.id}`}
                icon={action.id.startsWith('new-') ? Plus : action.icon}
                label={action.label}
                onSelect={() => run(action.run)}
              />
            ))}
          </Command.Group>
        )}

        {visiblePreferences.length > 0 && (
          <Command.Group heading="Preferences" className={groupClass}>
            {visiblePreferences.map((action) => (
              <Row
                key={action.id}
                value={`action-${action.id}`}
                icon={action.icon}
                label={action.label}
                onSelect={() => run(action.run)}
              />
            ))}
          </Command.Group>
        )}
      </Command.List>

      <div className="flex items-center gap-4 border-t border-gray-100 bg-gray-50/70 px-4 py-2 text-[11px] text-gray-500 dark:border-gray-800 dark:bg-gray-950/40 dark:text-gray-400">
        <span className="flex items-center gap-1.5">
          <kbd className="rounded border border-gray-200 bg-white px-1 font-sans dark:border-gray-700 dark:bg-gray-800">↑</kbd>
          <kbd className="rounded border border-gray-200 bg-white px-1 font-sans dark:border-gray-700 dark:bg-gray-800">↓</kbd>
          to move
        </span>
        <span className="flex items-center gap-1.5">
          <kbd className="flex h-[18px] items-center rounded border border-gray-200 bg-white px-1 dark:border-gray-700 dark:bg-gray-800">
            <CornerDownLeft className="h-3 w-3" aria-hidden="true" />
          </kbd>
          to open
        </span>
        <span className="ml-auto hidden sm:block">
          <kbd className="rounded border border-gray-200 bg-white px-1 font-sans dark:border-gray-700 dark:bg-gray-800">/</kbd>{' '}
          opens this from anywhere
        </span>
      </div>
    </Command.Dialog>
  );
}
